/*
  Queen's product page behaviour, rebuilt without jQuery
  ======================================================

  Queen leans on three jQuery pieces this theme does not carry:

    elevatezoom            main-image magnifier
    option_selection.js    variant switching off a hidden <select>
    kumi.script.js         thumbnail swapping

  The magnifier is gone - it is a jQuery plugin with no vanilla equivalent
  worth 40KB, and the main image still links to the full-size file. The other
  two are reimplemented below against Queen's own markup and class names, so
  queen-product.css applies to all of it unchanged.

  Everything else on the buy box - add to cart, the cart drawer, errors,
  dynamic checkout - is Dawn's <product-form>, which wraps the real
  {% form 'product' %}. This file only keeps the hidden variant id, the price
  and the button state in sync with the selects.
*/

(function () {
  /* ------------------------------------------------------------------ */
  /* Gallery: click a thumb, swap the main image                        */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('queen-gallery')) {
    customElements.define(
      'queen-gallery',
      class QueenGallery extends HTMLElement {
        connectedCallback() {
          this.main = this.querySelector('#main-image');
          this.link = this.querySelector('[data-gallery-link]');
          this.thumbs = Array.from(this.querySelectorAll('.image-thumb'));
          if (!this.main || !this.thumbs.length) return;

          this.thumbs.forEach((thumb) => {
            thumb.addEventListener('click', (event) => {
              event.preventDefault();
              this.select(thumb);
            });
          });
        }

        select(thumb) {
          this.main.src = thumb.dataset.image;
          this.main.alt = thumb.querySelector('img')?.alt || this.main.alt;
          this.main.removeAttribute('srcset');
          if (this.link) this.link.href = thumb.dataset.imageFull || thumb.href;
          this.thumbs.forEach((t) => t.classList.toggle('active', t === thumb));
        }

        /* Called when a variant with its own image is chosen. */
        showImage(src) {
          if (!src) return;
          const match = this.thumbs.find(
            (t) => t.dataset.image && t.dataset.image.split('?')[0] === src.split('?')[0]
          );
          if (match) this.select(match);
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Variants: map the selected options back to a variant               */
  /* ------------------------------------------------------------------ */

  function initVariants(root) {
    const data = root.querySelector('script[data-queen-variants]');
    const idInput = root.querySelector('[data-queen-variant-id]');
    const selects = Array.from(root.querySelectorAll('[data-queen-option]'));
    const priceEl = root.querySelector('[data-queen-price]');
    const submit = root.querySelector('[data-queen-submit]');
    if (!data || !idInput || !selects.length || idInput.dataset.queenInitialized) return;
    idInput.dataset.queenInitialized = 'true';

    let variants;
    try {
      variants = JSON.parse(data.textContent);
    } catch (e) {
      return; // leave the form as server-rendered rather than half-wire it
    }

    const gallery = root.querySelector('queen-gallery');

    /*
      Prices arrive already formatted by Liquid's `money` filter, so the
      shop's own currency format is used verbatim. Reconstructing it in JS
      would mean guessing at the symbol, separators and placement - and a
      product page that shows the wrong price is worse than one that shows
      none.
    */

    function apply() {
      const chosen = selects.map((s) => s.value);
      const variant = variants.find((v) =>
        chosen.every((value, i) => v.options[i] === value)
      );

      if (!variant) {
        idInput.value = '';
        idInput.setAttribute('disabled', 'disabled');
        if (submit) {
          submit.setAttribute('disabled', 'disabled');
          const label = submit.querySelector('span');
          if (label) label.textContent = 'Unavailable';
        }
        return;
      }

      idInput.value = variant.id;
      idInput.removeAttribute('disabled');
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      const barcode = root.querySelector('[data-queen-barcode]');
      const barcodeRow = root.querySelector('[data-queen-barcode-row]');
      if (barcode) barcode.textContent = variant.barcode || '';
      if (barcodeRow) barcodeRow.hidden = !variant.barcode;
      if (window.DeliveryDateRangeData?.product) {
        window.DeliveryDateRangeData.product.inventory_quantity = variant.inventory_quantity;
        window.DeliveryDateRangeData.product.sku = variant.sku;
      }

      if (submit) {
        const label = submit.querySelector('span');
        if (variant.available) {
          submit.removeAttribute('disabled');
          if (label) label.textContent = submit.dataset.addLabel || label.textContent;
        } else {
          submit.setAttribute('disabled', 'disabled');
          if (label) label.textContent = submit.dataset.soldOutLabel || 'Sold out';
        }
      }

      if (priceEl) priceEl.textContent = variant.price;
      if (gallery && variant.image) gallery.showImage(variant.image);

      /* Keep the URL shareable, the way Shopify's own picker does. */
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    selects.forEach((select) => select.addEventListener('change', apply));

    /* Remember the initial labels so they can be restored. */
    if (submit) {
      const label = submit.querySelector('span');
      submit.dataset.addLabel = 'Add to cart';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Variant dropdowns: custom listbox, native select as source of truth */
  /* ------------------------------------------------------------------ */

  function initCustomSelects(root) {
    root.querySelectorAll('[data-queen-custom-select]').forEach((control) => {
      if (control.dataset.queenInitialized) return;

      const nativeSelect = control.querySelector('[data-queen-option]');
      const trigger = control.querySelector('[data-queen-select-trigger]');
      const valueLabel = control.querySelector('[data-queen-select-value]');
      const menu = control.querySelector('[data-queen-select-menu]');
      const options = Array.from(control.querySelectorAll('[data-queen-select-option]'));
      if (!nativeSelect || !trigger || !valueLabel || !menu || !options.length) return;

      control.dataset.queenInitialized = 'true';
      let typeahead = '';
      let typeaheadTimer;

      function selectedOption() {
        return options.find((option) => option.dataset.value === nativeSelect.value) || options[0];
      }

      function syncFromNative() {
        const selected = selectedOption();
        valueLabel.textContent = selected ? selected.querySelector('span').textContent.trim() : nativeSelect.value;
        trigger.disabled = nativeSelect.disabled;
        options.forEach((option) => {
          option.setAttribute('aria-selected', String(option === selected));
        });
      }

      function focusOption(option) {
        if (!option) return;
        try {
          option.focus({ preventScroll: true });
        } catch (error) {
          option.focus();
        }
        option.scrollIntoView({ block: 'nearest' });
      }

      function positionMenu() {
        control.classList.remove('opens-up');
        const controlRect = control.getBoundingClientRect();
        const roomBelow = window.innerHeight - controlRect.bottom;
        const roomAbove = controlRect.top;
        if (roomBelow < menu.offsetHeight + 12 && roomAbove > roomBelow) {
          control.classList.add('opens-up');
        }
      }

      function open(preferredOption) {
        if (trigger.disabled || control.classList.contains('is-open')) return;
        control.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        menu.hidden = false;
        positionMenu();
        focusOption(preferredOption || selectedOption());
      }

      function close(restoreFocus) {
        if (!control.classList.contains('is-open')) return;
        control.classList.remove('is-open', 'opens-up');
        trigger.setAttribute('aria-expanded', 'false');
        menu.hidden = true;
        if (restoreFocus) trigger.focus();
      }

      function choose(option) {
        if (!option) return;
        const changed = nativeSelect.value !== option.dataset.value;
        nativeSelect.value = option.dataset.value;
        syncFromNative();
        if (changed) nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        close(true);
      }

      function moveFocus(current, amount) {
        const index = Math.max(0, options.indexOf(current));
        focusOption(options[(index + amount + options.length) % options.length]);
      }

      function focusByText(key) {
        window.clearTimeout(typeaheadTimer);
        typeahead += key.toLocaleLowerCase();
        typeaheadTimer = window.setTimeout(() => { typeahead = ''; }, 650);
        const match = options.find((option) =>
          option.querySelector('span').textContent.trim().toLocaleLowerCase().startsWith(typeahead)
        );
        if (match) focusOption(match);
      }

      trigger.addEventListener('click', () => {
        if (control.classList.contains('is-open')) close(false);
        else open();
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          open(options[options.length - 1]);
        } else if (event.key === 'Escape') {
          close(false);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          open();
          focusByText(event.key);
        }
      });

      options.forEach((option) => {
        option.addEventListener('click', () => choose(option));
        option.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveFocus(option, 1);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveFocus(option, -1);
          } else if (event.key === 'Home') {
            event.preventDefault();
            focusOption(options[0]);
          } else if (event.key === 'End') {
            event.preventDefault();
            focusOption(options[options.length - 1]);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            choose(option);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close(true);
          } else if (event.key === 'Tab') {
            close(false);
          } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            focusByText(event.key);
          }
        });
      });

      document.addEventListener('pointerdown', (event) => {
        if (!control.contains(event.target)) close(false);
      });
      window.addEventListener('resize', () => {
        if (control.classList.contains('is-open')) positionMenu();
      });
      nativeSelect.addEventListener('change', syncFromNative);

      const fieldLabel = control.closest('.selector-wrapper')?.querySelector('label');
      if (fieldLabel) fieldLabel.htmlFor = trigger.id;
      nativeSelect.tabIndex = -1;
      nativeSelect.setAttribute('aria-hidden', 'true');
      syncFromNative();
      trigger.hidden = false;
      control.classList.add('is-ready');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Quantity: accessible custom stepper                                */
  /* ------------------------------------------------------------------ */

  function initQuantity(root) {
    root.querySelectorAll('[data-queen-quantity]').forEach((control) => {
      if (control.dataset.queenInitialized) return;

      const input = control.querySelector('input[type="number"]');
      const buttons = Array.from(control.querySelectorAll('[data-queen-quantity-change]'));
      if (!input || !buttons.length) return;

      control.dataset.queenInitialized = 'true';

      function numberAttribute(name) {
        if (input.getAttribute(name) === null || input.getAttribute(name) === '') return null;
        const value = Number(input.getAttribute(name));
        return Number.isFinite(value) ? value : null;
      }

      function updateButtons() {
        const value = Number(input.value);
        const min = numberAttribute('min');
        const max = numberAttribute('max');

        buttons.forEach((button) => {
          const direction = Number(button.dataset.queenQuantityChange);
          button.disabled = Number.isFinite(value) && (
            (direction < 0 && min !== null && value <= min) ||
            (direction > 0 && max !== null && value >= max)
          );
        });
      }

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const direction = Number(button.dataset.queenQuantityChange);
          if (!direction) return;

          if (input.value === '') input.value = input.min || '1';

          try {
            if (direction > 0) input.stepUp();
            else input.stepDown();
          } catch (error) {
            const min = numberAttribute('min') ?? 1;
            const step = numberAttribute('step') ?? 1;
            const current = Number(input.value);
            input.value = String(Math.max(min, (Number.isFinite(current) ? current : min) + direction * step));
          }

          updateButtons();
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      input.addEventListener('input', updateButtons);
      input.addEventListener('change', updateButtons);
      updateButtons();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Buy box relocation                                                  */
  /* ------------------------------------------------------------------ */

  /*
    Queen's buy box only exists once. Below 992px, kumi.script.js detaches
    #product-header and appends it to #product-header-first inside the
    details column; above 992px it moves back to #product-header-last, which
    is `visible-md visible-lg`.

    Without this the box is simply hidden on phones and there is no add to
    cart at all, so it is not optional. Moving a <form> in the DOM preserves
    its state and its submit behaviour, which is why Queen can get away with
    a single instance rather than rendering two.
  */
  function initBuyBoxMove(root) {
    const box = root.querySelector('#product-header');
    const first = root.querySelector('#product-header-first');
    const last = root.querySelector('#product-header-last');
    if (!box || !first || !last) return;

    const narrow = window.matchMedia('(max-width: 991px)');

    function place() {
      const target = narrow.matches ? first : last;
      if (box.parentElement !== target) target.appendChild(box);
    }

    place();
    if (narrow.addEventListener) narrow.addEventListener('change', place);
    else narrow.addListener(place); // Safari < 14
  }

  function boot() {
    const holder = document.getElementById('cbb-holder');
    if (holder && !holder.dataset.initialized) {
      holder.dataset.initialized = 'true';
      const moveRecommendations = () => {
        const widget = document.querySelector('.cbb-also-bought-container');
        if (widget && !holder.contains(widget)) {
          holder.appendChild(widget);
          window.dispatchEvent(new Event('resize'));
        }
      };
      moveRecommendations();
      const observer = new MutationObserver(moveRecommendations);
      observer.observe(document.body, {childList:true, subtree:true});
      setTimeout(() => observer.disconnect(), 20000);
    }
    const roots = document.querySelectorAll('.qn-product');
    if (roots.length) {
      roots.forEach((root) => {
        initVariants(root);
        initCustomSelects(root);
        initQuantity(root);
        initBuyBoxMove(root);
      });
    } else {
      initVariants(document);
      initCustomSelects(document);
      initQuantity(document);
      initBuyBoxMove(document);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', boot);
})();
